import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'

import { QUERY_KEYS } from 'constants/constants'
import { useUserContext } from '../context'
import { mutation } from '../graphql'
import {
  readCsvExportSettings,
  withCsvExportMeta
} from '../helpers/csv-export-settings'
import { normalizeMeta } from '../helpers/employee'

/**
 * Gemerkte Exportauswahl pro Nutzer und Tabelle.
 *
 * Geschrieben wird nur beim Absenden eines Exports, nicht bei jedem Klick.
 * Damit entfallen Debounce, Busy-Guard und In-Flight-Zaehler aus
 * use-table-column-settings. Was bleiben muss, ist der Rest dieses Musters:
 * den Meta-Stand erst beim Schreiben aus dem react-query-Cache lesen, dann
 * optimistisch in den Cache schreiben und erst danach updateSpace rufen.
 * Es gibt mehrere Stellen im Repo, die customer_dashboard_meta als Ganzes aus
 * ihrem Render-Snapshot zurueckschreiben. Ohne diesen Pfad verliert der Export
 * seine Auswahl, sobald eine davon dazwischenfunkt.
 */
export const useCsvExportSettings = ({ tableId }) => {
  const { activeSpace } = useUserContext()
  const queryClient = useQueryClient()

  const stored = useMemo(
    () =>
      readCsvExportSettings(
        normalizeMeta(activeSpace?.customer_dashboard_meta),
        tableId
      ),
    [activeSpace?.customer_dashboard_meta, tableId]
  )

  const { mutate } = useMutation({
    mutationFn: ({ input }) => mutation({ mutation: 'updateSpace', input })
  })

  const persist = useCallback(
    ({ selectedIds, resolvableIds, profile }) => {
      const id = activeSpace?.id
      if (!id) return

      const key = QUERY_KEYS.activeSpaceById(id)
      const cached = queryClient.getQueryData(key)

      const customer_dashboard_meta = withCsvExportMeta(
        normalizeMeta(cached?.customer_dashboard_meta),
        tableId,
        { selectedIds, resolvableIds, profile }
      )

      // Einen laufenden getSpace verwerfen, seine spaete Antwort wuerde den
      // optimistischen Eintrag wieder ueberschreiben.
      queryClient.cancelQueries({ queryKey: key })
      queryClient.setQueryData(key, (prev) =>
        prev ? { ...prev, customer_dashboard_meta } : prev
      )

      // Fire and forget, ein fehlgeschlagener Write darf den Download nicht
      // aufhalten. Die Auswahl ist eine Bequemlichkeit, kein Ergebnis.
      mutate({ input: { id, customer_dashboard_meta } })
    },
    [activeSpace?.id, queryClient, tableId, mutate]
  )

  return { stored, persist }
}
